/**
 * The one predicate that decides whether a note doc is safe to write over.
 *
 * Every seed path — the Supabase→DO legacy migration, a transcription deck's
 * generated notes, anything future — asks this before writing, on web and on
 * mobile. It gates a DESTRUCTIVE write, so two copies drifting apart means one
 * platform overwriting notes the other protects; that is why it lives here and
 * not inline at each call site (it was inlined at two).
 *
 * Dependency-free on purpose: importable from React Native, a DOM/WebView
 * bundle, and the web app. Do NOT re-export from `./index`, which pulls in the
 * PDF generator.
 */

/** Minimal structural shape — avoids pinning to a Tiptap major. */
interface NoteDocJSON {
  content?: unknown[];
}

/**
 * Is the note doc "effectively empty" — nothing at all, or just the single
 * empty paragraph Tiptap/y-prosemirror writes into a fresh Y.Doc when the
 * editor mounts before any content is seeded?
 *
 * `true` means safe to (re)seed over. Anything richer is real user content and
 * must be left alone.
 *
 * This is deliberately more permissive than a bare `hasAny()` length check: a
 * stray empty paragraph used to read as content and block the seed, leaving
 * users with a permanently empty note.
 */
export function isNoteDocEffectivelyEmpty(json: unknown): boolean {
  if (!json || typeof json !== 'object') return true;
  const content = (json as NoteDocJSON).content;
  if (!Array.isArray(content) || content.length === 0) return true;
  if (content.length === 1) {
    const only = content[0] as { type?: string; content?: unknown[] };
    if (only?.type === 'paragraph' && (!only.content || only.content.length === 0)) {
      return true;
    }
  }
  return false;
}

/**
 * Minimal structural shape of a LIVE ProseMirror doc — the same
 * pin-to-no-Tiptap-major rule as {@link NoteDocJSON} above, and what keeps this
 * module importable from a DOM/WebView bundle that must not pull prosemirror
 * types.
 */
interface LiveNoteDoc {
  textContent: string;
  descendants: (
    fn: (node: { isAtom: boolean; isText: boolean }) => boolean | void,
  ) => void;
}

/**
 * Does the editor's CURRENT doc read as "nothing written yet" — no text and no
 * non-text atom, so an image-only note counts as written?
 *
 * This is the predicate that gates the get-started row (`NotesGenerateRow`).
 * Distinct from {@link isNoteDocEffectivelyEmpty}, which answers a different
 * question — "is this doc safe to overwrite with a seed" — off serialized JSON.
 *
 * It lives here, beside its sibling, for the same reason that one does: both
 * platforms render the same shared row, so the condition deciding WHEN it
 * appears has to be one implementation. It was briefly forked (web's
 * `get-started-toolbar`, mobile's `notes-editor`) and a change to what counts as
 * content would have reached only one of them.
 */
export function isEditorDocEffectivelyEmpty(doc: LiveNoteDoc): boolean {
  if (doc.textContent.trim().length > 0) return false;
  let hasAtom = false;
  doc.descendants((node) => {
    if (hasAtom) return false;
    if (node.isAtom && !node.isText) {
      hasAtom = true;
      return false;
    }
  });
  return !hasAtom;
}
